import React, { useEffect } from 'react'
import {
  Badge,
  BADGE_KIND_SECONDARY,
  BADGE_TYPE_DEFAULT,
  BADGE_TYPE_INFO,
  BADGE_TYPE_ERROR,
  BADGE_TYPE_WARNING,
  Button,
  BUTTON_VARIANT_NEUTRAL,
  BUTTON_SIZE_SM,
  ContextMenu,
  CONTEXT_MENU_SIZE_SM,
  IconArrow1Down,
  IconBan,
  IconCheck,
  IconInfo,
  IconNotification,
  IconNotificationOff,
  IconNotificationPaused,
  IconWarningTriangle,
  makeButtonWithMenu,
  Flex,
  FlexItem,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT
} from 'mailup-web-components'

import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import ReactTooltip from 'react-tooltip'
import styled from '@emotion/styled'

export const ICON_BAN = 'ban'
export const ICON_ALERT = 'alert'
export const ICON_INFO = 'info'
export const ICON_CHECK = 'check'

export const ALERT_ENABLE = 'enable'
export const ALERT_SILENT = 'silent'
export const ALERT_DISABLE = 'disable'

const CheckItem = ({
  children,
  icon,
  title,
  badge,
  alert,
  callbackAlert,
  description,
  itemKey,
  'data-mu-test': dataAttributeTest
}) => {
  useEffect(() => {
    ReactTooltip.rebuild()
  }, [])

  const intl = useIntl()

  const makeBadgeType = () => {
    if (icon === ICON_BAN) {
      return BADGE_TYPE_ERROR
    }
    if (icon === ICON_ALERT) {
      return BADGE_TYPE_WARNING
    }
    if (icon === ICON_INFO) {
      return BADGE_TYPE_INFO
    }

    return BADGE_TYPE_DEFAULT
  }

  const ButtonComponent = () => (
    <Button kind={BUTTON_VARIANT_NEUTRAL} size={BUTTON_SIZE_SM}>
      <Flex columnGap='8px'>
        {intl.formatMessage({
          id: 'Load.EmailCheckUp.AlertButton'
        })}
        <StyledIconDown />
      </Flex>
    </Button>
  )

  const MenuComponent = () => {
    const items = [
      {
        label: (
          <>
            <Flex
              alignItems='center'
              justifyContent='flex-start'
              columnGap='8px'
            >
              <IconNotification />
              {intl.formatMessage({
                id: 'Load.EmailCheckUp.AlertActionEnable'
              })}
            </Flex>
          </>
        ),
        value: 'ENABLE',
        onClick: e => alert !== ALERT_ENABLE && callbackAlert(e)
      },
      {
        label: (
          <>
            <Flex
              alignItems='center'
              justifyContent='flex-start'
              columnGap='8px'
            >
              <IconNotificationPaused />
              {intl.formatMessage({
                id: 'Load.EmailCheckUp.AlertActionSilent'
              })}
            </Flex>
          </>
        ),
        value: 'SILENT',
        onClick: e => alert !== ALERT_SILENT && callbackAlert(e)
      },
      {
        label: (
          <>
            <Flex
              alignItems='center'
              justifyContent='flex-start'
              columnGap='8px'
            >
              <IconNotificationOff />
              {intl.formatMessage({
                id: 'Load.EmailCheckUp.AlertActionDisable'
              })}
            </Flex>
          </>
        ),
        value: 'DISABLE',
        onClick: e => alert !== ALERT_DISABLE && callbackAlert(e)
      }
    ]

    return (
      <ContextMenu
        items={items}
        size={CONTEXT_MENU_SIZE_SM}
        selected={alert.toUpperCase()}
      />
    )
  }

  const ButtonWithMenu = makeButtonWithMenu(ButtonComponent, MenuComponent)

  return (
    <StyledContainer
      key={itemKey}
      data-mu-test={dataAttributeTest}
      className='check-item--container'
    >
      <Flex justifyContent='flex-start' width='100%'>
        <FlexItem width='35px'>
          <IconWrapper fontSize={icon === ICON_CHECK && '16px'} icon={icon}>
            {icon === ICON_BAN && <IconBan />}
            {icon === ICON_CHECK && <IconCheck />}
            {icon === ICON_ALERT && <IconWarningTriangle />}
            {icon === ICON_INFO && <IconInfo />}
          </IconWrapper>
        </FlexItem>
        <FlexItem width='calc(100% - 35px)'>
          <Flex justifyContent='space-between' alignItems='flex-start'>
            <Text variant={TEXT_VARIANT_BOLD}>{title} </Text>
            {badge && (
              <Badge
                type={makeBadgeType()}
                kind={BADGE_KIND_SECONDARY}
                text={badge}
              />
            )}
            {alert && (
              <Flex columnGap='8px' alignItems='center'>
                <IconWrapper
                  fontSize='13px'
                  data-tip={
                    alert === ALERT_ENABLE
                      ? intl.formatMessage({
                          id: 'Load.EmailCheckUp.AlertEnable'
                        })
                      : alert === ALERT_SILENT
                      ? intl.formatMessage({
                          id: 'Load.EmailCheckUp.AlertSilent'
                        })
                      : intl.formatMessage({
                          id: 'Load.EmailCheckUp.AlertDisable'
                        })
                  }
                >
                  {alert === ALERT_ENABLE && <IconNotification />}
                  {alert === ALERT_SILENT && <IconNotificationPaused />}
                  {alert === ALERT_DISABLE && <IconNotificationOff />}
                </IconWrapper>
                <StyledButtonWithMenuContainer>
                  <ButtonWithMenu direction='down' />
                </StyledButtonWithMenuContainer>
              </Flex>
            )}
          </Flex>
        </FlexItem>
      </Flex>
      {description && (
        <Flex margin='8px 0 0 35px'>
          <Text htmlElement='span' variant={TEXT_VARIANT_LIGHT}>
            {description}
          </Text>
        </Flex>
      )}
      {children && <span>{children}</span>}
    </StyledContainer>
  )
}

CheckItem.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.oneOf([ICON_BAN, ICON_ALERT, ICON_CHECK, ICON_INFO]),
  title: PropTypes.string,
  badge: PropTypes.string,
  alert: PropTypes.oneOf([ALERT_ENABLE, ALERT_DISABLE, ALERT_SILENT]),
  description: PropTypes.any,
  itemKey: PropTypes.string,
  callbackAlert: PropTypes.func,
  'data-mu-test': PropTypes.string
}

const StyledIconDown = styled(IconArrow1Down)`
  font-size: 9px;
`

const StyledButtonWithMenuContainer = styled.span`
  .c-button-with-menu__menu {
    right: -8px;
  }

  li {
    .c-svg-icon {
      align-self: baseline !important;
    }
  }
`

const StyledContainer = styled.div`
  + .check-item--container {
    border-top: 1px solid var(--geyser);
    margin-top: 16px;
    padding-top: 16px;
  }
`

const IconWrapper = styled.span`
  color: ${({ icon }) => {
    switch (icon) {
      case ICON_ALERT:
        return 'var(--sun)'
      case ICON_BAN:
        return 'var(--sunset-orange)'
      case ICON_CHECK:
        return 'var(--mantis)'
      case ICON_INFO:
        return 'var(--java)'
      default:
        return 'var(--boulder)'
    }
  }};
  font-size ${({ fontSize }) => fontSize || '18px'};
`
export default CheckItem
